import { UIMessage } from 'ai'
import { atomWithStorage as atom } from 'jotai/utils'
import type { ChartHypothesesVisibility } from 'components/business/hypotheses/chartHypothesesStore'
import {
  BusinessSalesPeriodFilter,
  BusinessSalesProductFilter,
  SellersWorkScheme,
  ISO8601DateTimeString,
  WebauthnAuthenticator,
  ProductListFilterStorage,
  BusinessUnitEconomicsCalculationsFormStorage,
  BidderCampaignStatAggregateMetrics,
  WebinarBanner,
} from 'lib/dto'
import { MallCode } from 'configs/malls'
import { ThemeMode } from './colors'

type LocalStorageBanners = {
  [key: `${'inner' | 'landing'}:${string}:AdClosed:times`]: number
  [key: `${'inner' | 'landing'}:${string}:AdClosed:date`]: ISO8601DateTimeString
}

export type LocalStorage = LocalStorageBanners & {
  tableColumnSettings: {
    version: 1
    tables: Record<
      string,
      {
        columnOrder: string[]
        columnVisibility: Record<string, boolean>
        columnSizing: Record<string, number>
      }
    >
  }
  oldBrowserWarningShown: boolean
  sidebarSize: 'large' | 'compact' | 'auto'
  'analyticsPeriodFilter:wb': BusinessSalesPeriodFilter
  'analyticsProductFilter:wb': BusinessSalesProductFilter
  'analyticsSellersWorkScheme:wb': SellersWorkScheme
  'analyticsPeriodFilter:ozon': BusinessSalesPeriodFilter
  'analyticsProductFilter:ozon': BusinessSalesProductFilter
  'analyticsSellersWorkScheme:ozon': SellersWorkScheme
  'analyticsPeriodFilter:beru': BusinessSalesPeriodFilter
  'analyticsProductFilter:beru': BusinessSalesProductFilter
  'analyticsSellersWorkScheme:beru': SellersWorkScheme
  analyticsByOrderDate: boolean
  assistantHistory: UIMessage[]
  supportUnreadState: Record<string, string | null>
  guideGroupsState: Record<string, boolean>
  primeCostModalSettings: {
    fileType: 'bySkus' | 'bySupplierSkus' | 'byBarcodes'
    withSizes: boolean
  }
  comparisonProductIds: Record<MallCode, string[]>
  businessProductListVisibleColumns: string[]
  seasonalityLabeledCount: number
  categoriesInventoryType: 'merchant' | 'marketplace' | null
  webauthnAuthenticators: WebauthnAuthenticator[]
  closedWebauthnUpgradeBanner: boolean
  changelogState: {
    lastClosed: ISO8601DateTimeString | null
    lastOpened: ISO8601DateTimeString | null
    lastChecked: ISO8601DateTimeString | null
    lastCheckLatestEntryDate: ISO8601DateTimeString | null
  }
  'unitEconomicsCalc:timesUpdated': number
  'unitEconomicsCalc:wbAdditionalCommissionPercent': number
  'unitEconomicsCalc:formByMall': Partial<
    Record<MallCode, BusinessUnitEconomicsCalculationsFormStorage>
  >
  'cookieConsent:accepted': boolean
  'surveyNotifier:closed': boolean
  webinarBannerCache: {
    banner: WebinarBanner | null
    requestedAt: number
  }
  theme: ThemeMode
  lastSeenDocumentCreatedAt: ISO8601DateTimeString | null
  recentlyDisabledRepricerProducts: { [mall: string]: string[] }
  productListSettings: ProductListFilterStorage
  'products:listViewMode': 'list' | 'table'
  'keywords:listViewMode': 'list' | 'table'
  'repricer:viewMode': 'products' | 'tags'
  positionsSelectedProducts: Partial<Record<MallCode, number[]>>
  productSearchHistory: Partial<Record<MallCode, number[]>>
  chartHypothesesVisibility: ChartHypothesesVisibility
  bidderAggregateChartType: keyof BidderCampaignStatAggregateMetrics
  'tools:marketplace-requirements-malls': Array<'wb' | 'ozon' | 'beru'>
  'tools:image-generation:imageModel':
    | 'gemini-3-pro-image'
    | 'gemini-3-pro-image-preview'
    | 'gemini-nano-banana-2.1'
    | 'gemini-3.1-flash-image-preview'
  'tools:image-generation:imageSize': '1K' | '2K' | '4K'
  'tools:image-generation:aspectRatio': string | null
  'tools:image-generation:mode': 'single' | 'batch'
  'tools:video-generation:aspectRatio': '16:9' | '9:16'
  'tools:video-generation:resolution': '720p' | '1080p' | '4K'
  'tools:video-generation:durationSeconds': number
  businessReviewsRuleGenerateTextLength: Partial<
    Record<'greetTexts' | 'mainTexts' | 'endTexts', number>
  >
  businessReviewsRuleGenerateTextTone: Partial<
    Record<
      'greetTexts' | 'mainTexts' | 'endTexts',
      'auto' | 'professional' | 'friendly' | 'enthusiastic' | 'luxury'
    >
  >
}

export const storage = {
  get: <T extends keyof LocalStorage>(key: T): LocalStorage[T] | null => {
    if (typeof window === 'undefined') return null
    const value = localStorage.getItem(key)
    if (value === null) return null
    return JSON.parse(value)
  },

  set: <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) => {
    if (typeof window === 'undefined') return
    const storedValue = JSON.stringify(value)
    localStorage.setItem(key, storedValue)
  },

  remove: <T extends keyof LocalStorage>(key: T) => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(key)
  },
}

export const atomWithStorage = <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) => {
  return atom(key, value)
}
